import pkceChallenge from 'pkce-challenge';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { RefreshToken } from '@crossnokaye/typescript-sdk/models/components';
import { useToaster } from '@crossnokaye/ux-common';

import { AppConfigs } from '@/config';
import { useAuthPopup } from '@/features/tokens/hooks/useAuthPopup';
import {
  fetchUserRefreshTokens,
  transformListUserRefreshTokensResponse,
} from '@/features/tokens/hooks/useListUserRefreshTokens';
import { useSetRefreshTokenAttributes } from '@/features/tokens/hooks/useSetRefreshTokenAttributes';
import {
  constructAuthorizationUrl,
  getRedirectUri,
} from '@/features/tokens/utils/constructAuthorizationUrl';
import { validateAuthResponse } from '@/features/tokens/utils/validateAuthResponse';
import { useAuthUser } from '@/providers/auth/auth-user-provider';

const accessTokenBaseUrl = AppConfigs.isLocal ? '/' : AppConfigs.atlasEnvUrl.toString();

// The login endpoint does not report the identifier of the refresh token it just
// minted, so the new token has to be spotted by diffing the user's token list
// against a snapshot taken beforehand. Auth0 does not always list the token on
// the first read, hence the retries.
const NEW_TOKEN_POLL_ATTEMPTS = 5;
const NEW_TOKEN_POLL_DELAY_MS = 500;

const NAMING_FAILED_MESSAGE =
  'Token created, but its name could not be saved. Rename it from the table below.';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useCreateRefreshToken = () => {
  const { user } = useAuthUser();
  const { openAuthPopup } = useAuthPopup();
  const queryClient = useQueryClient();
  const { addErrorToast } = useToaster();
  const { mutateAsync: updateTokenAttributes } = useSetRefreshTokenAttributes();

  const listTokens = useCallback(async (): Promise<RefreshToken[]> => {
    const response = await fetchUserRefreshTokens({ userId: user?.id ?? '' });
    return transformListUserRefreshTokensResponse(response);
  }, [user?.id]);

  const findCreatedToken = useCallback(
    async (knownTokenIds: Set<string>): Promise<RefreshToken | undefined> => {
      for (let attempt = 0; attempt < NEW_TOKEN_POLL_ATTEMPTS; attempt++) {
        if (attempt > 0) {
          await delay(NEW_TOKEN_POLL_DELAY_MS);
        }

        const tokens = await listTokens();
        const createdToken = tokens.find((token) => !knownTokenIds.has(token.id));

        if (createdToken) {
          return createdToken;
        }
      }

      return undefined;
    },
    [listTokens]
  );

  const initiateAuthRequest = useCallback(
    async (newTokenName: string, newTokenDescription?: string): Promise<string | undefined> => {
      // Snapshot before the popup so the token minted below is the only unknown id.
      const knownTokenIds = new Set((await listTokens()).map((token) => token.id));

      const { code_verifier, code_challenge } = await pkceChallenge();
      const state = window.crypto.randomUUID();

      const authUrl = constructAuthorizationUrl({ code_challenge, state });

      const response = await openAuthPopup(authUrl);
      const code = validateAuthResponse(response, state);

      const loginResponse = await fetch(`${accessTokenBaseUrl}api/login/v3/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          grant_type: 'authorization_code',
          code,
          code_verifier,
          redirect_uri: getRedirectUri(),
        }),
      });

      if (!loginResponse.ok) {
        throw new Error('Failed to exchange code for tokens');
      }

      const tokens = await loginResponse.json();

      const createdToken = await findCreatedToken(knownTokenIds);

      if (!createdToken) {
        addErrorToast(NAMING_FAILED_MESSAGE);
      } else {
        try {
          await updateTokenAttributes({
            userId: user.id,
            tokenId: createdToken.id,
            setRefreshTokenAttributesRequestBody: {
              name: newTokenName,
              description: newTokenDescription,
            },
          });
        } catch {
          addErrorToast(NAMING_FAILED_MESSAGE);
        }
      }

      queryClient.invalidateQueries({ queryKey: ['iam', 'ListUserRefreshTokens'] });

      return tokens.refresh_token;
    },
    [
      addErrorToast,
      findCreatedToken,
      listTokens,
      openAuthPopup,
      queryClient,
      updateTokenAttributes,
      user.id,
    ]
  );

  return {
    initiateAuthRequest,
  };
};
